import { useQueries, useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { FunctionComponent, useEffect, useMemo } from 'react';

import useIsMobile from '../../../hooks/use-is-mobile';
import useToken from '../../../hooks/use-token';
import { fetchFaqs } from '../../../services/faqs';
import { fetchBrands } from '../../../services/vehicles';
import { allSavedCarsAtom } from '../../../state/vehicle';
import getUserSavedCars from '../../../utils/react-query/query-options/get-user-saved-cars';
import Reviews from '../../modules/reviews';
import ValueProposition from '../../modules/value-proposition';
import { useAppContext } from '../../providers/app-context';
import Curve from '../../ui/curve';
import CategoriesList from '../categories/categories-list';
import BrandList from '../vehicle/brands/brand-list';

import AboutUs from './about-us';
import Banner from './banner';
import Categories from './categories';
import Questions from './questions';
import HomeSchema from './seo';
import { catalogueGroupsAtom } from '../../../state/selection';
import getLasEntries from '../../../utils/react-query/query-options/get-last-entries';
import getLastVehicles from '../../../utils/react-query/query-options/get-last-vehicles';
import LastEntries from '../v2/vehicle/last-entries';
import LastVehicles from '../v2/vehicle/last-vehicles';

const TopBrands = dynamic(() => import('./top-brands'));
const TrustBox = dynamic(() => import('../../modules/trustpilot/new'), { ssr: false });

const Home: FunctionComponent<TPageHome> = ({ groupsSort }) => {
  const { locale = 'en', route } = useRouter();
  const { tenantConfig, brand } = useAppContext();
  const isMobile = useIsMobile();
  const csrftoken = useToken();
  const {
    features: { isBrandEnabled }, detailedPrice
  } = useAppContext();
  const isUK = process.env.NEXT_PUBLIC_HOST?.includes('.co.uk');
  const isEnglish = isUK || locale === 'en';
  const [{ data: brands }, { data: faqs }] = useQueries({
    queries: [
      {
        queryKey: ['brands', locale],
        queryFn: ({ queryKey: [, locale] }) => fetchBrands({ locale }),
        staleTime: 10000,
        keepPreviousData: true,
        enabled: isBrandEnabled,
      },
      {
        queryKey: ['faqs', locale, 'home'],
        queryFn: ({ queryKey: [, locale, slug] }) =>
          fetchFaqs({ locale, slug }),
        staleTime: 10000,
        keepPreviousData: true,
      },
    ],
  });

  const { data: savedVehicles, isLoading: isVehiclesLoading } = useQuery(
    ...getUserSavedCars({ locale, csrftoken }),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: 'always',
      refetchInterval: 60000,
      refetchOnMount: true,
      staleTime: 10000,
    }
  );

  const { data: lastEntries } = useQuery(
    ...getLasEntries({ locale, detailed_prices: !!detailedPrice, page: 'home' }),
    {
      keepPreviousData: true,
      enabled: isEnglish
    }
  );

  const { data: lastVehicles } = useQuery(
    ...getLastVehicles({ locale, page: 'home' }),
    {
      keepPreviousData: true,
      enabled: isEnglish
    }
  );

  const brandList = useMemo(() => {
    if (brands) {
      return brands.children?.filter((b) => b.logo_url && b.logo_white_url);
    }

    return [];
  }, [brands]);

  const defaultisOpenValue = useMemo(() => {
    return !isMobile;
  }, [isMobile]);

  const [, setAllSavedCars] = useAtom(allSavedCarsAtom);

  useEffect(() => {
    setAllSavedCars(savedVehicles);
  }, [savedVehicles, setAllSavedCars]);

  return (
    <main>
      <HomeSchema faqsForSchema={faqs} />
      <Banner />
      <div className='h-[60px] container bg-white mx-auto flex items-center justify-center'><TrustBox /></div>
      <ValueProposition variant="SECONDARY" isHome={true} showTitle={false} />
      <Curve className="h-4 lg:h-6 scale-y text-neutral-10 " />
      <Categories groups={groupsSort} />
      <CategoriesList
        groupsSort={groupsSort}
        hideCategoriesList={true}
        defaultisOpenValue={isEnglish ? false : defaultisOpenValue}
      ></CategoriesList>
      {!(isEnglish && (lastEntries && !!lastEntries.length)) && <AboutUs />}
      {isEnglish && lastEntries && !!lastEntries.length && (
        <>
          <Curve className="h-4 lg:h-6 scale-y-invert lg:scale-x-invert text-neutral-10 " />
          <LastEntries products={lastEntries} />
          <Curve className="h-4 lg:h-6 scale-y text-neutral-10 " />
        </>
      )}
      <TopBrands brands={brandList} />
      <BrandList
        brands={brands?.children}
        searchByPlateAvailable={false}
        isScrollToTopAvailable={false}
        hideBrandList={true}
        defaultisOpenValue={isEnglish ? false : defaultisOpenValue}
        savedCarsAvailable={false}
      />
      {lastVehicles && !!lastVehicles.length && <>
        <Curve className="h-4 lg:h-6 scale-y-invert lg:scale-x-invert text-neutral-10 " />
        <LastVehicles products={lastVehicles} />
        <Curve className="h-4 lg:h-6 scale-y text-neutral-10 " />
      </>}
      {(isEnglish && (lastEntries && !!lastEntries.length)) && <AboutUs backgroundColor={'bg-white'} showCurve={false} />}
      <Questions faqs={faqs} />
      <Reviews />
    </main>
  );
};

export default Home;
